export const NAV_LINKS = [
  { label: "Dashboard", href: "#dashboard" }, 
  { label: "Pipeline", href: "#pipeline" },
  { label: "Features", href: "#features" },
  { label: "FAQ", href: "#faq" },
];

export const STAGES = [
  { name: "Prospecting" },
  { name: "Lead Qualification" },
  { name: "Discovery / Pain Points" },
  { name: "Demo / Pitch" },
  { name: "Proposal" },
  { name: "Negotiation" },
  { name: "Closed" },
  { name: "Customer" },
];

export const STATS = [
  { value: "8", label: "Pipeline stages from prospect to customer" },
  { value: "5", label: "Ways to log work: Calls, Emails, Meetings, Notes and Field work" },
  { value: "10", label: "Contact persons per company" },
  { value: "3", label: "Roles: Sales representative, Manager and Admin" },
];

export const HIGHLIGHTS = [
  {
    icon: "Building2",
    title: "Every company in one place",
    text: "Companies, their contact persons and their details live together, not scattered across spreadsheets and inboxes.",
  },
  {
    icon: "TrendingUp",
    title: "A clear view of every deal",
    text: "See where each company sits in the pipeline and what it is worth, from prospect to closed deal.",
  },
  {
    icon: "FileText",
    title: "A full history of every conversation",
    text: "Calls, emails, meetings, notes and field work are logged against the company, so anyone can pick up where you left off.",
  },
];

export const STEPS = [
  {
    title: "Prospect",
    text: "Add a company with its main contact, phone, email and industry, then add more contact persons whenever you need them.",
    icon: "Building2",
  },
  {
    title: "Validate Prospect",
    text: "A manager reviews each new prospect and records a Qualified or Not Qualified decision. Qualified companies move forward.",
    icon: "ShieldCheck",
  },
  {
    title: "Pipeline Process",
    text: "Log calls, emails, meetings, notes and field work, create tasks and follow-ups, and move the company through the stages.",
    icon: "TrendingUp",
  },
  {
    title: "Deal Proposal",
    text: "Add a deal to the company with its value and move the company to the Proposal stage.",
    icon: "FileText",
  },
  {
    title: "Negotiation & Deal Closed",
    text: "Move the company through Negotiation, then close the deal as won or lost with the final amount and a note.",
    icon: "Briefcase",
  },
  {
    title: "Customer Management",
    text: "Won companies move on to Customer. Keep their contacts, history and tasks together, and watch revenue and pipeline value on your dashboard.",
    icon: "Users",
  },
];

export const FEATURES = [
  {
    icon: "Building2",
    title: "Company Management",
    text: "Every company in one searchable list with its main contact, plus up to 10 contact persons per company, including a primary contact.",
  },
  {
    icon: "LayoutDashboard",
    title: "Dashboard Insights",
    text: "Revenue from won deals, active deals, tasks due today and total companies at a glance, plus pipeline value, deals by stage and company status.",
  },
  {
    icon: "TrendingUp",
    title: "Visual Sales Pipeline",
    text: "Follow every company across eight stages, from Prospecting to Customer, on a board the whole team can read at a glance.",
  },
  {
    icon: "ShieldCheck",
    title: "Prospect Validation",
    text: "Managers review new prospects and record a Qualified or Not Qualified decision with a research note.",
  },
  {
    icon: "Briefcase",
    title: "Deals & Closing",
    text: "Add deals to a company, then close them as won or lost with the final amount and a note about what made the difference.",
  },
  {
    icon: "FileText",
    title: "Interaction History",
    text: "Log calls, emails, meetings, notes and field work. Notes can be Research Mapping, Follow-up or Office Visit, and edits are clearly marked.",
  },
  {
    icon: "Calendar",
    title: "Task Management",
    text: "Create tasks with a priority, due date and assignee, link them to a company, and view them as a list or a board.",
  },
  {
    icon: "Mail",
    title: "AI & Email Management",
    text: "Write to a company's registered contacts with To, CC, a formatted body and a PDF attachment, with AI integration, without leaving IZSales.",
  },
  {
    icon: "Mic",
    title: "Call Voice Recording",
    text: "Record a call from the interaction form and keep the recording with the company's history, so nothing said is lost.",
  },
];

export const ROLES = [
  {
    title: "Sales Representative",
    text: "Your daily workspace for managing companies and conversations.",
    points: [
      "Add and manage companies",
      "Log calls, emails, meetings, notes and field work",
      "Create tasks and follow-ups",
      "Move companies through the pipeline",
    ],
  },
  {
    title: "Sales Manager",
    text: "Keep the pipeline healthy and the right companies moving.",
    points: [
      "Verify new prospects",
      "Record qualification decisions",
      "View archived companies",
      "Restore archived companies",
    ],
  },
  {
    title: "Admin & Super Admin",
    text: "Oversee the system with extra controls.",
    points: [
      "Delete interactions",
      "Admin area for users",
      "Activity logs",
      "Full system oversight",
    ],
  },
];

export const FAQS = [
  {
    q: "What is IZSales?",
    a: "IZSales is a web-based sales CRM from IZSoftwares. It brings your companies, pipeline, deals, tasks and conversations together in one place, so the whole team works from the same up-to-date picture.",
  },
  {
    q: "Is there a free version?",
    a: "Yes. IZSales is free for the first 3 months. During that time your team can use IZSales with its own companies, contact persons, pipeline, deals, tasks and interactions, so you can see how it fits the way you sell before you decide. When the 3 months end, pricing details are shared when you contact the admin. To start your free 3 months, use the Get started button on the pricing page to contact IZSoftwares.",
    link: { label: "Go to the pricing page", to: "/pricing" },
  },
  {
    q: "Are there other types of pricing?",
    a: "Yes. IZSales has two plans. Business covers the core sales workflow: Company management, the Visual sales pipeline and many more . Enterprise includes everything in Business, plus the Super Admin for managing users, Activity logs, Managing Interactions, AI integration and many more.",
    link: { label: "View the plans on the pricing page", to: "/pricing" },
  },
  {
    q: "Who can verify new prospects?",
    a: "Sales managers review new prospects in the Prospecting stage and record a Qualified or Not Qualified decision with an optional research note. Qualified companies move on to Lead Qualification.",
  },
  {
    q: "How many contact persons can a company have?",
    a: "Up to 10 contact persons per company, and one of them can be marked as the primary contact.",
  },
  {
    q: "What are the pipeline stages?",
    a: "Prospecting, Lead Qualification, Discovery / Pain Points, Demo / Pitch, Proposal, Negotiation, Closed and Customer. Every company moves through them on the visual pipeline board.",
  },
  {
    q: "What can I record about a company?",
    a: "Calls, emails, meetings, notes and field work. Notes can be Research Mapping, Follow-up or Office Visit, and edited notes are clearly marked in the company history.",
  },
  {
    q: "How does email work on IZSales?",
    a: "You write to a company's registered contacts from inside IZSales, with To, CC, a formatted body and a PDF attachment, so you never have to leave IZSales. The email is kept in the company's history, so the whole team can see it.",
  },
  {
    q: "How do I get an account?",
    a: "Accounts are created by your administrator. If you do not have one yet, contact your administrator.",
  },
];

export const FOOTER_COLUMNS = [
  {
    title: "Product",
    links: [
      { label: "About IZSales", href: "#about" },
      { label: "Sales pipeline", href: "#pipeline" },
      { label: "Dashboard", href: "#dashboard" },
      { label: "Interactions", href: "#interactions" },
      { label: "All features", href: "#features" },
    ],
  },
  {
    title: "Who it's for",
    links: [
      { label: "Sales representatives", href: "#roles" },
      { label: "Sales managers", href: "#roles" },
      { label: "Admins", href: "#roles" },
    ],
  },
  {
    title: "Get started",
    links: [
      { label: "How it works", href: "#how-it-works" },
      { label: "FAQ", href: "#faq" },
    ],
  },
];